import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { Observable, throwError } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { ClientSettings } from "src/model/class/client-settings";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { Settings } from "src/model/class/settings";
import {
  clientSettingsUpdateAction,
  clientSettingsUpdateSpecificSettingAction,
} from "src/state/actions/client-settings.actions";
import {
  serverSettingsLoadCompleteAction,
  serverSettingsUpdateShowStatusBarAction,
} from "src/state/actions/server-settings.action";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { EventService } from "./event.service";
import { LoggerService } from "./logger.service";
import { ThemeService } from "./theme.service";

@Injectable({
  providedIn: "root",
})
export class SettingsService {
  private _logContext: string = "Settings.service";

  constructor(
    private _http: HttpClient,
    private _store: Store,
    private _eventService: EventService,
    private _themeService: ThemeService,
    private _loggerService: LoggerService
  ) { }

  /**
   * Ask to the logged-in plant's server in order to receive "general" settings for the current user
   * @returns
   */
  public getSettings(): Observable<Settings> {
    return this._http
      .get<Settings>(
        environment.httpServerRoot + AppSettings.API_URL_USER + "/settings"
      )
      .pipe(
        map((serverSettings: Settings) => {
          return new Settings(serverSettings);
        }),
        tap((serverSettings: Settings) =>
          this._store.dispatch(
            serverSettingsLoadCompleteAction({ serverSettings })
          )
        ),
        // tap((serverSettings: Settings) => {
        //   if (!this._themeService.isDigitalPlace) {
        //     this.checkWanConnectionBlock(serverSettings.AllowWANConnection);
        //   }
        // }),
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  /**
   * Update specified setting associated, in the context of a peculiar logged-in plant, to a peculiar user (who has executed the loggin-in)
   *
   * @param settingName Name of the updated setting; it must be included both in the local model "settings" (as a property) and in the server-side
   * Schema and DB-table (as a column-name).
   *
   * @param settingValue New value the specified setting shall be updated with; MUST be casted as STRING
   *
   * @param propagateChanges Flag value; if it is needed that the changes to the specified setting are propagated, it is set to TRUE. Otherwise, it is set to FALSE.
   * DEFAULT: TRUE.
   *
   * @returns An observable; if the API call is correctly resolved, the caller is THEN free to act on this knowledge.
   */
  public updateUserSetting(
    settingName: string,
    settingValue: string
  ): Promise<any> {
    const putData = {
      name: settingName,
      value: settingValue,
    };

    return this._http
      .put(
        environment.httpServerRoot + AppSettings.API_URL_USER + "/setting",
        putData
      )
      .pipe(
        tap(async (resultUpdate) => {
          this.createStoreActionbySettingsName(settingName, settingValue);
        }),
        catchError((error) => {
          return throwError(error);
        })
      )
      .toPromise();
  }

  /**
   * Save current settings of the device on which the app is running; this is done both in the local storage and in the publicly accessible observable.
   * @param clientSettings
   */
  public async saveClientSettings(
    clientSettings: ClientSettings
  ): Promise<void> {
    this._store.dispatch(clientSettingsUpdateAction({ clientSettings }));
  }

  /**
   * Save specific setting with own new value
   *
   * @param settingsName Setting name to update
   * @param settingsValue Setting value to update
   */
  public async saveSpecificClientSettingsValue(
    settingsName: string,
    settingsValue: any
  ) {
    this._store.dispatch(
      clientSettingsUpdateSpecificSettingAction({ settingsName, settingsValue })
    );
  }

  /**
   *  Send event for allow/disallow wan connection to currently selected plant plant, based on value defined in "serverSettings"
   *
   * @param allowWanConnection Allow wan connection boolean value
   */
  private checkWanConnectionBlock(allowWanConnection: boolean): void {
    let blockWanConnection: boolean = false;

    if (allowWanConnection) {
      blockWanConnection = allowWanConnection;
    }

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "checkWanConnectionBlock()",
      "allowWanConnection from serverSetting: " +
      allowWanConnection +
      " | Block WAN usage: " +
      blockWanConnection
    );

    this._eventService.OnPlantReceivedWanBlockResponse.next(blockWanConnection);
  }

  /**
   * Create specific store action by settings name
   *
   * @param settingName Setting name
   * @param settingValue Setting value
   */
  private createStoreActionbySettingsName(
    settingName: string,
    settingValue: string
  ): void {
    switch (settingName) {
      case "ShowStatusBar":
        this._store.dispatch(
          serverSettingsUpdateShowStatusBarAction({
            showStatusbar: settingValue.toString().toUpperCase() === "TRUE",
          })
        );
        break;
    }
  }
}
